/* In-place radix-2 FFT with cached twiddle and bit-reversal tables. */

export type FFT = (re: Float64Array, im: Float64Array, inverse: boolean) => void;

const fftCache = new Map<number, FFT>();

export function getFFT(n: number): FFT {
  const cached = fftCache.get(n);
  if (cached) return cached;
  const levels = Math.round(Math.log2(n));
  if (1 << levels !== n) throw new Error('FFT size must be a power of two');
  const half = n >> 1;
  const cosT = new Float64Array(half);
  const sinT = new Float64Array(half);
  for (let i = 0; i < half; i++) {
    cosT[i] = Math.cos((2 * Math.PI * i) / n);
    sinT[i] = Math.sin((2 * Math.PI * i) / n);
  }
  const rev = new Uint32Array(n);
  for (let i = 0; i < n; i++) {
    let x = i;
    let r = 0;
    for (let j = 0; j < levels; j++) {
      r = (r << 1) | (x & 1);
      x >>>= 1;
    }
    rev[i] = r;
  }
  const fft: FFT = (re, im, inverse) => {
    for (let i = 0; i < n; i++) {
      const j = rev[i];
      if (j > i) {
        let t = re[i];
        re[i] = re[j];
        re[j] = t;
        t = im[i];
        im[i] = im[j];
        im[j] = t;
      }
    }
    for (let size = 2; size <= n; size <<= 1) {
      const h = size >> 1;
      const step = n / size;
      for (let i = 0; i < n; i += size) {
        for (let j = i, k = 0; j < i + h; j++, k += step) {
          const l = j + h;
          const c = cosT[k];
          const s = inverse ? sinT[k] : -sinT[k];
          const tre = re[l] * c - im[l] * s;
          const tim = re[l] * s + im[l] * c;
          re[l] = re[j] - tre;
          im[l] = im[j] - tim;
          re[j] += tre;
          im[j] += tim;
        }
      }
    }
    if (inverse) {
      const inv = 1 / n;
      for (let i = 0; i < n; i++) {
        re[i] *= inv;
        im[i] *= inv;
      }
    }
  };
  fftCache.set(n, fft);
  return fft;
}
